import { useState, useEffect, useCallback } from 'react';
import './PressePage.css';
import logo from '../../assets/images/rocaille-logo.png';
import { imageSections, documents } from './pressAssets';

/* Passwort fuer den Pressebereich – hier aendern. */
const PRESSE_PASSWORT = 'rocaille2026';

const STORAGE_KEY = 'rocaille-presse-auth';

function PasswordGate({ onUnlock }) {
  const [value, setValue] = useState('');
  const [error, setError] = useState(false);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (value.trim().toLowerCase() === PRESSE_PASSWORT.toLowerCase()) {
      onUnlock();
    } else {
      setError(true);
    }
  };

  return (
    <div className="presse-gate">
      <div className="presse-gate__card">
        <img src={logo} alt="Rocaille" className="presse-gate__logo" />
        <span className="presse-gate__label">Pressebereich</span>
        <p className="presse-gate__text">
          Dieser Bereich ist geschützt. Bitte geben Sie das Passwort ein, das Sie von uns
          erhalten haben.
        </p>
        <form className="presse-gate__form" onSubmit={handleSubmit}>
          <input
            type="password"
            className={`presse-gate__input${error ? ' presse-gate__input--error' : ''}`}
            placeholder="Passwort"
            value={value}
            onChange={(e) => { setValue(e.target.value); setError(false); }}
            autoFocus
            aria-label="Passwort"
          />
          <button type="submit" className="btn btn--gold presse-gate__submit">
            Zugang
          </button>
        </form>
        {error && (
          <span className="presse-gate__error">Passwort nicht korrekt.</span>
        )}
        <a href="/" className="presse-gate__back">Zurück zur Website</a>
      </div>
    </div>
  );
}

function DownloadCard({ item }) {
  return (
    <a className="presse-card" href={item.src} download={item.file}>
      <div className="presse-card__thumb-wrap">
        <img src={item.src} alt={item.title} className="presse-card__thumb" loading="lazy" />
        <span className="presse-card__overlay">Herunterladen</span>
      </div>
      <span className="presse-card__title">{item.title}</span>
    </a>
  );
}

function DocumentRow({ item }) {
  return (
    <div className="presse-doc">
      <span className="presse-doc__icon">PDF</span>
      <span className="presse-doc__title">{item.title}</span>
      <div className="presse-doc__actions">
        <a
          className="presse-doc__action"
          href={item.src}
          target="_blank"
          rel="noopener noreferrer"
        >
          Ansehen
        </a>
        <a className="presse-doc__action" href={item.src} download={item.file}>
          Herunterladen
        </a>
      </div>
    </div>
  );
}

function PressePage() {
  const [unlocked, setUnlocked] = useState(() => {
    try {
      return window.sessionStorage.getItem(STORAGE_KEY) === 'true';
    } catch {
      return false;
    }
  });

  useEffect(() => {
    document.title = 'Pressebereich – Rocaille Düsseldorf';
    // Bestehendes robots-Meta ueberschreiben, damit der Bereich nicht indexiert wird
    const meta = document.querySelector('meta[name="robots"]');
    if (!meta) return undefined;
    const previous = meta.content;
    meta.content = 'noindex, nofollow';
    return () => { meta.content = previous; };
  }, []);

  const handleUnlock = useCallback(() => {
    try {
      window.sessionStorage.setItem(STORAGE_KEY, 'true');
    } catch {
      /* sessionStorage nicht verfuegbar – Zugang gilt nur fuer diese Sitzung */
    }
    setUnlocked(true);
  }, []);

  const handleLock = () => {
    try {
      window.sessionStorage.removeItem(STORAGE_KEY);
    } catch {
      /* ignorieren */
    }
    setUnlocked(false);
  };

  if (!unlocked) {
    return <PasswordGate onUnlock={handleUnlock} />;
  }

  return (
    <div className="presse">
      <header className="presse__topbar">
        <a href="/" className="presse__home">
          <img src={logo} alt="Rocaille" className="presse__logo" />
        </a>
        <button className="presse__lock" onClick={handleLock}>Sperren</button>
      </header>

      <main className="presse__inner">
        <div className="presse__header">
          <span className="presse__label">Presse &amp; Medien</span>
          <h1 className="presse__title">Pressebereich</h1>
          <div className="presse__divider" />
          <p className="presse__intro">
            Bildmaterial, Auszeichnungen und Dokumente zur freien Verwendung in der
            redaktionellen Berichterstattung über das Rocaille. Wir bitten um Nennung
            der Quelle: Rocaille Düsseldorf.
          </p>
        </div>

        {imageSections.map((section) => (
          <section key={section.id} className="presse__section">
            <h2 className="presse__section-title">{section.title}</h2>
            <div className="presse__grid">
              {section.items.map((item) => (
                <DownloadCard key={item.id || item.file} item={item} />
              ))}
            </div>
          </section>
        ))}

        <section className="presse__section">
          <h2 className="presse__section-title">Dokumente</h2>
          <div className="presse__docs">
            {documents.map((item) => (
              <DocumentRow key={item.id || item.file} item={item} />
            ))}
          </div>
        </section>

        <section className="presse__contact">
          <h2 className="presse__section-title">Pressekontakt</h2>
          <p className="presse__contact-text">
            Für weiteres Material, Interviewanfragen oder Fotos in höherer Auflösung
            erreichen Sie uns unter:
          </p>
          <div className="presse__contact-links">
            <a href="mailto:genuss@rocaille.de">genuss@rocaille.de</a>
            <a href="tel:+4921197711737">+49 (0)211 97711737</a>
          </div>
        </section>
      </main>

      <footer className="presse__footer">
        <p>&copy; {new Date().getFullYear()} Rocaille Düsseldorf</p>
        <a href="/" className="presse__footer-link">Zurück zur Website</a>
      </footer>
    </div>
  );
}

export default PressePage;
